import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import type { ButtonIcon } from './type/button-icon.type';
import { EHorizontalPosition } from '@common/enums';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { buttonIconTemplates } from '@common/icons/button-icons';
import { ERadioGroupType } from '@ui/sp-radio-group';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpRadioGroupComponent } from '../../ui/sp-radio-group/src/sp-radio-group.component';
import { TranslateModule } from '@ngx-translate/core';

const pickButtonIcon = (value: Partial<ButtonIcon> | null | undefined): ButtonIcon => ({
    isActive: value?.isActive,
    icon: value?.icon,
    position: value?.position,
});

@Component({
    selector: 'sp-button-icon',
    templateUrl: './sp-button-icon.component.html',
    styleUrls: ['./sp-button-icon.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpSwitchComponent, BsDropdownModule, SpRadioGroupComponent, TranslateModule],
})
export class SpButtonIconComponent {
    protected readonly sanitizer = inject(DomSanitizer);

    public readonly buttonIcon = input<ButtonIcon | null>(null);
    public readonly valueChangeEvent = output<ButtonIcon>();

    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EHorizontalPosition = EHorizontalPosition;

    public readonly buttonIconList = Object.entries(buttonIconTemplates);

    private readonly model = signal<ButtonIcon>(pickButtonIcon(null));

    public readonly isActive = computed(() => Boolean(this.model().isActive));
    public readonly icon = computed(() => this.model().icon);
    public readonly position = computed(() => this.model().position);

    constructor() {
        effect(() => this.model.set(pickButtonIcon(this.buttonIcon())));
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public setIsActive(isActive: boolean): void {
        this.patch({ isActive });
    }

    public setIconPosition(value: string): void {
        this.patch({ position: value as ButtonIcon['position'] });
    }

    public setIcon(icon: string): void {
        this.patch({ icon });
    }

    private patch(patch: Partial<ButtonIcon>): void {
        this.model.update((model) => ({ ...model, ...patch }));
        this.valueChangeEvent.emit(this.model());
    }
}
